import { ArrowDown } from 'lucide-react';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  latestUserMessageId: string | null;
  latestNonUserMessageId: string | null;
  children: React.ReactNode;
}

export function MessageScrollContainer({ latestUserMessageId, latestNonUserMessageId, children }: Props) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const spacerRef = useRef<HTMLDivElement | null>(null);

  const isInitialRenderRef = useRef(true);

  const [showBanner, setShowBanner] = useState(false);
  const prevNonUserMessageIdRef = useRef<string | null>(latestNonUserMessageId);

  const isLastMessageVisible = () => {
    const container = containerRef.current;
    const list = listRef.current;
    if (!container || !list) return true;
    const lastChild = list.lastElementChild as HTMLElement | null;
    if (!lastChild) return true;
    const containerRect = container.getBoundingClientRect();
    const lastChildRect = lastChild.getBoundingClientRect();
    return lastChildRect.top < containerRect.bottom - 10;
  };

  const updateLayout = () => {
    const container = containerRef.current;
    const list = listRef.current;
    const spacer = spacerRef.current;

    if (!container || !list || !spacer) return;

    // Spacer = exact amount needed so maxScrollTop = userMsgOffset (user message scrollable to top, nothing more).
    let padding = 0;
    if (latestUserMessageId) {
      const lastUserMsgEl = container.querySelector(`[data-message-id="${latestUserMessageId}"]`) as HTMLElement | null;
      if (lastUserMsgEl) {
        const contentBelowMsgTop = list.getBoundingClientRect().bottom - lastUserMsgEl.getBoundingClientRect().top;
        // 32 = paddingTop (16) + paddingBottom (16) from the container's p-4 class
        padding = Math.max(0, container.clientHeight - contentBelowMsgTop - 32);
      }
    }

    spacer.style.height = `${padding}px`;
  };

  const updateLayoutRef = useRef(updateLayout);
  useLayoutEffect(() => {
    updateLayoutRef.current = updateLayout;
  });

  // Run on React renders (streaming updates, details toggles)
  useLayoutEffect(() => {
    updateLayout();
  }, [children]);

  // Handle new user message (Reset padding and scroll to it)
  useEffect(() => {
    if (!latestUserMessageId) return;

    const container = containerRef.current;

    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      // Initial load: scroll to bottom without adding extra padding
      if (container) {
        requestAnimationFrame(() => {
          container.scrollTop = container.scrollHeight;
        });
      }
      return;
    }

    const target = container?.querySelector(`[data-message-id="${latestUserMessageId}"]`) as HTMLElement;

    if (target) {
      updateLayout();

      requestAnimationFrame(() => {
        target.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
      });
    }
  }, [latestUserMessageId]);

  useEffect(() => {
    if (!latestNonUserMessageId) return;
    if (latestNonUserMessageId === prevNonUserMessageIdRef.current) return;
    prevNonUserMessageIdRef.current = latestNonUserMessageId;
    if (!isLastMessageVisible()) setShowBanner(true);
  }, [latestNonUserMessageId]);

  const handleScrollToBanner = () => {
    const list = listRef.current;
    const lastChild = list?.lastElementChild as HTMLElement | null;
    lastChild?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    setShowBanner(false);
  };

  // DOM Event Listeners
  useEffect(() => {
    const container = containerRef.current;
    const list = listRef.current;
    if (!container || !list) return;

    const handleResize = () => updateLayoutRef.current();
    const ro = new ResizeObserver(handleResize);
    ro.observe(container);
    ro.observe(list);

    const onScroll = () => {
      updateLayoutRef.current();
      if (isLastMessageVisible()) setShowBanner(false);
    };

    container.addEventListener('scroll', onScroll, { passive: true });

    // Catch native details toggles
    container.addEventListener('toggle', handleResize, { capture: true, passive: true });

    return () => {
      ro.disconnect();
      container.removeEventListener('scroll', onScroll);
      container.removeEventListener('toggle', handleResize, { capture: true });
    };
  }, []);

  return (
    <div className="flex-1 relative overflow-hidden">
      <div
        ref={containerRef}
        className="absolute inset-0 overflow-y-auto p-4"
        style={{ overflowAnchor: 'none', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <div ref={listRef} data-message-list className="space-y-4">
          {children}
        </div>

        <div ref={spacerRef} className="shrink-0 pointer-events-none w-full" aria-hidden="true" />
      </div>

      {showBanner && (
        <div className="absolute bottom-1 left-0 right-0 z-10 flex justify-center pointer-events-none">
          <button
            type="button"
            onClick={handleScrollToBanner}
            className="pointer-events-auto flex items-center gap-1.5 rounded-full bg-white dark:bg-card border border-slate-200 dark:border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 shadow-md hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            <ArrowDown className="h-3.5 w-3.5" />
            {t('chatMessages.newMessage')}
          </button>
        </div>
      )}
    </div>
  );
}
